import { useTranslation } from "react-i18next";
import { getImage } from "../../services/ImageService.js";
import { CustomerQuote } from "../../components/CustomerQuote/CustomerQuote.js";
import { SectionCard } from "../../components/SectionCard/SectionCard.js";
import { SectionCardHeader } from "../../components/SectionCard/SectionCardHeader.js";
import { SectionCardHStack } from "../../components/SectionCard/SectionCardHStack.js";
import { SectionCardGrid } from "../../components/SectionCard/SectionCardGrid.js";
import { SectionCardVStack } from "../../components/SectionCard/SectionCardVStack.js";
import { SectionCardSubsection } from "../../components/SectionCard/SectionCardSubsection.js";
import { NavigationActionButton } from "../../components/NavigationActionButton/NavigationActionButton.js";
import {
    ReactCompareSlider,
    ReactCompareSliderImage,
} from "react-compare-slider";

export function DamageInventorySection() {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-24">
            <SectionCard
                id="damage-inventory"
                img="cards/damage-cropped.png"
                className="bg-brown"
                gradientClassName="brown-gradient"
            >
                <SectionCardHeader
                    title={t("home-damage-inventory-card-title")}
                    text={t("home-damage-inventory-card-text")}
                />
                <SectionCardGrid className="grid-cols-3">
                    <SectionCardHStack
                        className="col-span-3"
                        title={t("home-damage-inventory-card-section-1-title")}
                        text={t("home-damage-inventory-card-section-1-text")}
                    />
                    <SectionCardVStack
                        img="cards/drone-cropped.png"
                        title={t("home-damage-inventory-card-section-2-title")}
                        text={t("home-damage-inventory-card-section-2-text")}
                    />
                    <SectionCardVStack
                        img="cards/observations-cropped.png"
                        title={t("home-damage-inventory-card-section-3-title")}
                        text={t("home-damage-inventory-card-section-3-text")}
                    />
                    <SectionCardVStack
                        img="cards/everyday-inventory-cropped.png"
                        title={t("home-damage-inventory-card-section-4-title")}
                        text={t("home-damage-inventory-card-section-4-text")}
                    />
                </SectionCardGrid>
                <SectionCardVStack
                    title={t(
                        "home-damage-inventory-card-rapid-orthophoto-title",
                    )}
                >
                    <ReactCompareSlider
                        className="rounded-4xl"
                        itemOne={
                            <ReactCompareSliderImage
                                src={getImage("cards/IMG_1748.png")}
                                alt={t(
                                    "home-damage-inventory-card-rapid-orthophoto-title",
                                )}
                                loading="lazy"
                            />
                        }
                        itemTwo={
                            <ReactCompareSliderImage
                                src={getImage("cards/IMG_1747.png")}
                                alt={t(
                                    "home-damage-inventory-card-rapid-orthophoto-title",
                                )}
                                loading="lazy"
                            />
                        }
                    />
                </SectionCardVStack>
                <CustomerQuote
                    img={getImage("quotes/sara.png")}
                    className="bg-darkBrown"
                    quote={t("home-damage-inventory-customer-1")}
                    customer="Sara Sundin, Stora Enso Sverige"
                />
                <SectionCardSubsection className="bg-darkBrown">
                    <img
                        className="w-48 h-48 mb-8 lg:mr-16 lg:mb-0"
                        src={getImage("logos/storaenso-circle.png")}
                        alt="Stora Enso"
                        loading="lazy"
                    />
                    <div className="text-base gap-4 flex flex-col font-regular">
                        <div>
                            <div className="text-xl mt-0 font-semibold font-robotoCondensed">
                                {t("stora-enso-bark-beetle-title")}
                            </div>
                            <p>{t("stora-enso-bark-beetle-text")}</p>
                        </div>
                        <NavigationActionButton
                            className="my-0 !text-black !bg-white hover:!bg-[rgba(255,255,255,0.9)] w-full sm:w-min"
                            label={t("learn-more")}
                            href="/stora-enso-bark-beetle"
                        />
                    </div>
                </SectionCardSubsection>
                <SectionCardSubsection className="bg-darkBrown">
                    <img
                        className="w-48 h-48 mb-8 lg:mr-16 lg:mb-0"
                        src={getImage("logos/kopparfors-circle.png")}
                        alt="Kopparfors"
                        loading="lazy"
                    />
                    <div className="text-base gap-4 flex flex-col font-regular">
                        <div>
                            <div className="text-xl mt-0 font-semibold font-robotoCondensed">
                                {t("kopparfors-windfall-title")}
                            </div>
                            <p>{t("kopparfors-windfall-text")}</p>
                        </div>
                        <NavigationActionButton
                            className="my-0 !text-black !bg-white hover:!bg-[rgba(255,255,255,0.9)] w-full sm:w-min"
                            label={t("learn-more")}
                            href="/kopparfors-windfall"
                        />
                    </div>
                </SectionCardSubsection>
            </SectionCard>
            <SectionCard
                id="bark-beetle"
                className="bg-brown !gap-12"
                img="cards/bark-beetle-cropped.png"
                gradientClassName="brown-gradient"
            >
                <SectionCardVStack
                    title={t("home-bark-beetle-card-title")}
                    text={t("home-bark-beetle-card-text")}
                    className="mr-0 sm:mr-32 lg:mr-[36rem]"
                >
                    <NavigationActionButton
                        className="my-0 !text-black !bg-white hover:!bg-[rgba(255,255,255,0.9)] w-full sm:w-min"
                        label={t("learn-more")}
                        href="/bark-beetle"
                    />
                </SectionCardVStack>
            </SectionCard>
            <SectionCard
                id="windthrow"
                className="bg-brown !gap-12"
                img="cards/windthrow-cropped.png"
                gradientClassName="brown-gradient"
            >
                <SectionCardVStack
                    title={t("home-windthrow-card-title")}
                    text={t("home-windthrow-card-text")}
                    className="mr-0 sm:mr-32 lg:mr-[36rem]"
                >
                    <NavigationActionButton
                        className="my-0 !text-black !bg-white hover:!bg-[rgba(255,255,255,0.9)] w-full sm:w-min"
                        label={t("learn-more")}
                        href="/windthrow"
                    />
                </SectionCardVStack>
            </SectionCard>
            <SectionCard
                id="fire"
                className="bg-brown !gap-12"
                img="cards/fire-cropped.png"
                gradientClassName="brown-gradient"
            >
                <SectionCardVStack
                    title={t(
                        "home-wildfires-and-prescribed-burning-card-title",
                    )}
                    text={t("home-wildfires-and-prescribed-burning-card-text")}
                    className="mr-0 sm:mr-32 lg:mr-[36rem]"
                >
                    <NavigationActionButton
                        className="my-0 !text-black !bg-white hover:!bg-[rgba(255,255,255,0.9)] w-full sm:w-min"
                        label={t("learn-more")}
                        href="/fire"
                    />
                </SectionCardVStack>
            </SectionCard>
        </div>
    );
}
